import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Form,
  Button, Spinner, Alert,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import usersService from '../services/usersService';
import qnaService from '../services/qnaService';

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'المستخدمون', icon: 'bi-people-fill' },
];

const fmtDate = (v) => {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleString('ar-IQ', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return v;
  }
};

const InfoRow = ({ icon, label, value }) => (
  <div className="d-flex align-items-start gap-2 mb-2">
    <i className={`${icon} text-secondary mt-1`} style={{ flexShrink: 0, fontSize: '0.9rem' }} />
    <div>
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{label}</div>
      <div style={{ fontSize: '0.88rem', wordBreak: 'break-all' }}>{value || '-'}</div>
    </div>
  </div>
);

const StatCard = ({ icon, label, value, color }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="d-flex align-items-center gap-3 p-3">
      <div style={{
        width: 54, height: 54, borderRadius: '50%',
        background: color + '22', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={icon} style={{ fontSize: '1.4rem', color }} />
      </div>
      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
        <div className="text-muted small mt-1">{label}</div>
      </div>
    </Card.Body>
  </Card>
);

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [questionCounts, setQuestionCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [result, qna] = await Promise.all([
        usersService.getAll(),
        qnaService.getAll().catch(() => ({ questions: [] })),
      ]);
      setUsers(Array.isArray(result) ? result : []);
      const counts = {};
      for (const q of qna.questions || []) {
        const email = q.email?.trim().toLowerCase();
        if (email) counts[email] = (counts[email] || 0) + 1;
      }
      setQuestionCounts(counts);
    } catch (e) {
      setError('تعذّر تحميل البيانات: ' + (e.message || 'خطأ غير معروف'));
    } finally {
      setLoading(false);
    }
  }, []);

  const countFor = useCallback(
    (u) => questionCounts[u.email?.trim().toLowerCase()] || 0,
    [questionCounts],
  );

  useEffect(() => { load(); }, [load]);

  const platforms = useMemo(
    () => [...new Set(users.map(u => u.platform).filter(Boolean))],
    [users],
  );
  const providers = useMemo(
    () => [...new Set(users.map(u => u.provider).filter(Boolean))],
    [users],
  );

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return users.filter(u => {
      const matchSearch = !s || [u.fullName, u.email, u.whatsapp]
        .some(v => v?.toLowerCase().includes(s));
      const matchPlatform = !filterPlatform || u.platform === filterPlatform;
      const matchProvider = !filterProvider || u.provider === filterProvider;
      return matchSearch && matchPlatform && matchProvider;
    });
  }, [users, search, filterPlatform, filterProvider]);

  const stats = useMemo(() => ({
    total: users.length,
    verified: users.filter(u => u.emailVerified).length,
    disabled: users.filter(u => u.disabled).length,
    askers: users.filter(u => countFor(u) > 0).length,
  }), [users, countFor]);

  return (
    <Layout title="المستخدمون" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        {/* Stats */}
        <Row className="mb-4 g-3">
          <Col md={3} sm={6}>
            <StatCard icon="bi-people-fill" label="إجمالي المستخدمين" value={stats.total} color="#0d6efd" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard icon="bi-patch-check-fill" label="بريد موثّق" value={stats.verified} color="#198754" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard icon="bi-chat-dots-fill" label="مستخدمون سألوا" value={stats.askers} color="#6f42c1" />
          </Col>
          <Col md={3} sm={6}>
            <StatCard icon="bi-person-x-fill" label="محظور" value={stats.disabled} color="#dc3545" />
          </Col>
        </Row>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={5}>
                <Form.Control
                  placeholder="بحث بالاسم، البريد، واتساب..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </Col>
              <Col xs={6} md={2}>
                <Form.Select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}>
                  <option value="">كل المنصات</option>
                  {platforms.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Form.Select value={filterProvider} onChange={e => setFilterProvider(e.target.value)}>
                  <option value="">كل طرق التسجيل</option>
                  {providers.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={2}>
                <Button variant="outline-secondary" onClick={load} className="w-100" disabled={loading}>
                  <i className="bi bi-arrow-clockwise ms-1" />
                  تحديث
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill" />
            {error}
          </Alert>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-muted mt-3">جارٍ تحميل البيانات...</div>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th className="d-none d-md-table-cell" style={{ width: 45 }}>#</th>
                      <th>الاسم</th>
                      <th className="d-none d-md-table-cell">البريد الإلكتروني</th>
                      <th className="d-none d-md-table-cell">المنصة</th>
                      <th style={{ width: 90 }}>الأسئلة</th>
                      <th style={{ width: 110 }}>الحالة</th>
                      <th className="d-none d-md-table-cell" style={{ width: 130 }}>تاريخ التسجيل</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-5 text-muted">
                          <i className="bi bi-inbox fs-3 d-block mb-2" />
                          لا توجد نتائج مطابقة
                        </td>
                      </tr>
                    )}
                    {filtered.map((u, idx) => {
                      const isExpanded = expanded === u.uid;
                      return (
                        <React.Fragment key={u.uid}>
                          <tr
                            onClick={() => setExpanded(isExpanded ? null : u.uid)}
                            style={{
                              cursor: 'pointer',
                              background: isExpanded ? '#f0f4ff' : undefined,
                              borderRight: isExpanded ? '3px solid #0d6efd' : '3px solid transparent',
                            }}
                          >
                            <td className="d-none d-md-table-cell text-muted">{idx + 1}</td>
                            <td>
                              <div className="fw-semibold">{u.fullName || '-'}</div>
                              <div className="d-md-none text-muted" style={{ fontSize: '0.75rem' }}>
                                {u.email}
                              </div>
                            </td>
                            <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.85rem' }}>
                              {u.email || '-'}
                            </td>
                            <td className="d-none d-md-table-cell">
                              {u.platform && (
                                <span className="badge text-bg-light text-muted border" style={{ fontSize: '0.72rem' }}>
                                  {u.platform}
                                </span>
                              )}
                            </td>
                            <td>
                              {countFor(u) > 0 ? (
                                <span className="badge text-bg-primary" style={{ fontSize: '0.78rem' }}>
                                  <i className="bi bi-chat-dots ms-1" />
                                  {countFor(u)}
                                </span>
                              ) : (
                                <span className="text-muted" style={{ fontSize: '0.85rem' }}>—</span>
                              )}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {u.disabled ? (
                                <span className="badge text-bg-danger">محظور</span>
                              ) : u.emailVerified ? (
                                <span className="badge text-bg-success">موثّق</span>
                              ) : (
                                <span className="badge text-bg-secondary">غير موثّق</span>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell text-muted" style={{ fontSize: '0.8rem' }}>
                              {fmtDate(u.createdAt)}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  title="عرض أسئلة المستخدم"
                                  onClick={() => navigate(`/users/${u.uid}/questions`)}
                                >
                                  <i className="bi bi-chat-dots" />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  title={isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
                                  onClick={() => setExpanded(isExpanded ? null : u.uid)}
                                >
                                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                                </Button>
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr style={{ background: '#f8f9ff' }}>
                              <td colSpan={8} className="p-3 pt-2">
                                <Row className="g-3">
                                  <Col lg={6}>
                                    <Card className="border-0 bg-white shadow-sm h-100">
                                      <Card.Body className="p-3">
                                        <div className="fw-semibold text-secondary small mb-3">
                                          <i className="bi bi-person-lines-fill ms-1" />
                                          بيانات المستخدم
                                        </div>
                                        <InfoRow icon="bi-fingerprint" label="المعرّف (UID)" value={u.uid} />
                                        <InfoRow icon="bi-envelope" label="البريد الإلكتروني" value={u.email} />
                                        <InfoRow icon="bi-whatsapp" label="واتساب" value={u.whatsapp} />
                                        <InfoRow icon="bi-box-arrow-in-right" label="طريقة التسجيل" value={u.provider} />
                                        <InfoRow icon="bi-globe" label="المنصة" value={u.platform} />
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                  <Col lg={6}>
                                    <Card className="border-0 bg-white shadow-sm h-100">
                                      <Card.Body className="p-3">
                                        <div className="fw-semibold text-secondary small mb-3">
                                          <i className="bi bi-clock-history ms-1" />
                                          النشاط والتحقق
                                        </div>
                                        <InfoRow
                                          icon="bi-patch-check"
                                          label="البريد موثّق؟"
                                          value={u.emailVerified ? 'نعم' : 'لا'}
                                        />
                                        <InfoRow
                                          icon="bi-telephone"
                                          label="الهاتف موثّق؟"
                                          value={u.phoneVerified ? 'نعم' : 'لا'}
                                        />
                                        <InfoRow icon="bi-calendar-plus" label="تاريخ التسجيل" value={fmtDate(u.createdAt)} />
                                        <InfoRow icon="bi-calendar-check" label="آخر تسجيل دخول" value={fmtDate(u.lastLoginAt)} />
                                        <InfoRow icon="bi-chat-dots" label="عدد الأسئلة" value={countFor(u)} />
                                        <InfoRow icon="bi-camera-video" label="فيديوهات مُعجب بها" value={u.likedVideosCount ?? 0} />
                                        <InfoRow icon="bi-bookmark" label="فيديوهات محفوظة للمشاهدة لاحقاً" value={u.watchLaterVideosCount ?? 0} />
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                </Row>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {!loading && users.length > 0 && (
                <div className="text-muted small px-3 py-2 border-top">
                  عرض {filtered.length} من {users.length} مستخدم
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
}
